import DOMPurify from "isomorphic-dompurify";
import validator from "validator";

/**
 * 🔒 FRONTEND SECURITY UTILITIES
 * Implements all security measures from COMMUNITY_SECURITY_FIXES.md
 */

// ===== INPUT SANITIZATION & XSS PREVENTION =====

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} input - Raw HTML input
 * @param {Object} options - DOMPurify options
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (input, options = {}) => {
  if (!input || typeof input !== "string") return "";

  const defaultOptions = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "span",
      "div",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "blockquote",
      "code",
      "pre",
    ],
    ALLOWED_ATTR: ["class", "id"],
    FORBID_TAGS: ["script", "object", "embed", "iframe"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    ...options,
  };

  return DOMPurify.sanitize(input, defaultOptions);
};

/**
 * Sanitize plain text input (removes all HTML)
 * @param {string} input - Raw text input
 * @returns {string} Sanitized text
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== "string") return "";

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

/**
 * Validate and sanitize URL to prevent XSS through href attributes
 * @param {string} url - URL to validate
 * @returns {string|null} Sanitized URL or null if invalid
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== "string") return null;

  // Allow only http, https, and mailto protocols
  const allowedProtocols = ["http:", "https:", "mailto:"];

  try {
    const urlObj = new URL(url);
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return null;
    }
    return urlObj.toString();
  } catch {
    return null;
  }
};

// ===== FORM VALIDATION WITH SECURITY =====

/**
 * Validate community name with security checks
 * @param {string} name - Community name
 * @returns {string|null} Error message or null if valid
 */
export const validateCommunityName = (name) => {
  if (!name || typeof name !== "string") {
    return "Community name is required";
  }

  const sanitized = sanitizeInput(name.trim());

  if (sanitized.length < 3 || sanitized.length > 100) {
    return "Community name must be between 3 and 100 characters";
  }

  // Allow alphanumeric, spaces, and safe punctuation
  if (!/^[a-zA-Z0-9\s\-_.,!?()&]+$/.test(sanitized)) {
    return "Community name contains invalid characters";
  }

  // Check for reserved names
  const reservedNames = [
    "admin",
    "moderator",
    "system",
    "api",
    "root",
    "administrator",
  ];
  if (reservedNames.includes(sanitized.toLowerCase())) {
    return "This community name is reserved";
  }

  return null;
};

/**
 * Validate community description with security checks
 * @param {string} description - Community description
 * @returns {string|null} Error message or null if valid
 */
export const validateCommunityDescription = (description) => {
  if (!description || typeof description !== "string") {
    return "Description is required";
  }

  const sanitized = sanitizeInput(description.trim());

  if (sanitized.length < 10 || sanitized.length > 500) {
    return "Description must be between 10 and 500 characters";
  }

  return null;
};

/**
 * Validate thread title with security checks
 * @param {string} title - Thread title
 * @returns {string|null} Error message or null if valid
 */
export const validateThreadTitle = (title) => {
  if (!title || typeof title !== "string") {
    return "Title is required";
  }

  const sanitized = sanitizeInput(title.trim());

  if (sanitized.length < 5 || sanitized.length > 200) {
    return "Title must be between 5 and 200 characters";
  }

  return null;
};

/**
 * Validate message content with security checks
 * @param {string} content - Message content
 * @returns {string|null} Error message or null if valid
 */
export const validateMessageContent = (content) => {
  if (!content || typeof content !== "string") {
    return "Message is required";
  }

  const sanitized = sanitizeInput(content.trim());

  if (sanitized.length < 1 || sanitized.length > 1000) {
    return "Message must be between 1 and 1000 characters";
  }

  return null;
};

// ===== FILE UPLOAD SECURITY =====

/**
 * Validate image upload with security checks
 * @param {File} file - File to validate
 * @returns {boolean} True if valid, throws error if invalid
 */
export const validateImageUpload = (file) => {
  if (!file) {
    throw new Error("No file selected");
  }

  // Check file type
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
  ];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      "Invalid file type. Only JPG, PNG, WebP, and GIF files are allowed"
    );
  }

  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error("File too large. Maximum size is 5MB");
  }

  // Check file name for malicious patterns
  const maliciousPatterns = [
    /\.php$/i,
    /\.asp$/i,
    /\.jsp$/i,
    /\.exe$/i,
    /\.bat$/i,
    /\.cmd$/i,
  ];
  if (maliciousPatterns.some((pattern) => pattern.test(file.name))) {
    throw new Error("Invalid file name");
  }

  return true;
};

/**
 * Generate secure file name
 * @param {string} originalName - Original file name
 * @returns {string} Secure file name
 */
export const generateSecureFileName = (originalName) => {
  const sanitized = originalName.replace(/[^a-zA-Z0-9._-]/g, "");
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2);
  return `${timestamp}-${random}-${sanitized}`;
};

// ===== ERROR HANDLING & SECURITY LOGGING =====

/**
 * Handle API errors securely without exposing sensitive information
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const handleSecureError = (error) => {
  // Never expose sensitive error details to users
  if (error.response?.status === 403) {
    return "Access denied";
  } else if (error.response?.status === 429) {
    return "Too many requests. Please try again later.";
  } else if (error.response?.status === 400) {
    return "Invalid request. Please check your input.";
  } else if (error.response?.status === 401) {
    return "Authentication required. Please sign in.";
  } else if (error.response?.status === 404) {
    return "Resource not found";
  } else {
    return "An error occurred. Please try again.";
  }
};

/**
 * Log security events for monitoring
 * @param {string} event - Event type
 * @param {Object} details - Event details
 */
export const logSecurityEvent = (event, details) => {
  if (process.env.NODE_ENV === "production") {
    // In production, send to security monitoring service
    fetch("/api/security/log", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        event,
        details: sanitizeInput(JSON.stringify(details)),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.pathname,
      }),
    }).catch(() => {
      // Silent fail for security logging
    });
  } else {
    // In development, log to console
}
};

// ===== CONTENT SECURITY POLICY HELPERS =====

/**
 * Create nonce for inline scripts/styles
 * @returns {string} Random nonce
 */
export const generateNonce = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

// ===== RATE LIMITING HELPERS =====

/**
 * Client-side rate limiting tracker
 */
class RateLimiter {
  constructor() {
    this.attempts = new Map();
  }

  /**
   * Check if action is rate limited
   * @param {string} action - Action identifier
   * @param {number} limit - Max attempts
   * @param {number} windowMs - Time window in milliseconds
   * @returns {boolean} True if allowed
   */
  isAllowed(action, limit = 10, windowMs = 60000) {
    const now = Date.now();
    const key = action;

    if (!this.attempts.has(key)) {
      this.attempts.set(key, []);
    }

    const attempts = this.attempts.get(key);

    // Remove old attempts outside the window
    const validAttempts = attempts.filter((time) => now - time < windowMs);
    this.attempts.set(key, validAttempts);

    if (validAttempts.length >= limit) {
      logSecurityEvent("rate_limit_exceeded", {
        action,
        limit,
        window: windowMs,
      });
      return false;
    }

    // Record this attempt
    validAttempts.push(now);
    return true;
  }

  /**
   * Reset rate limit for an action
   * @param {string} action - Action identifier
   */
  reset(action) {
    this.attempts.delete(action);
  }
}

export const rateLimiter = new RateLimiter();

// ===== EMAIL & URL VALIDATION =====

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
  return validator.isEmail(email);
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export const isValidUrl = (url) => {
  return validator.isURL(url, {
    protocols: ["http", "https"],
    require_protocol: true,
  });
};

// ===== SECURE STORAGE HELPERS =====

/**
 * Set secure local storage item with expiration
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {number} ttl - Time to live in milliseconds
 */
export const setSecureStorage = (key, value, ttl = 3600000) => {
  const item = {
    value: sanitizeInput(JSON.stringify(value)),
    expiry: Date.now() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Get secure local storage item
 * @param {string} key - Storage key
 * @returns {any} Stored value or null if expired/not found
 */
export const getSecureStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return JSON.parse(parsed.value);
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

// ===== SECURITY HEADERS =====

/**
 * Get security headers for fetch requests
 * @returns {Object} Security headers
 */
export const getSecurityHeaders = () => {
  return {
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "1; mode=block",
    "Referrer-Policy": "strict-origin-when-cross-origin",
    "X-Requested-With": "XMLHttpRequest",
    "Cache-Control": "no-cache",
  };
};

// ===== EXPORT ALL UTILITIES =====

export default {
  sanitizeHtml,
  sanitizeInput,
  sanitizeUrl,
  validateCommunityName,
  validateCommunityDescription,
  validateThreadTitle,
  validateMessageContent,
  validateImageUpload,
  generateSecureFileName,
  handleSecureError,
  logSecurityEvent,
  generateNonce,
  rateLimiter,
  isValidEmail,
  isValidUrl,
  setSecureStorage,
  getSecureStorage,
  getSecurityHeaders,
};
